/**
 * June's Strategy Calls Slide
 * Grid of forward-looking market timing calls
 */

import React from 'react';
import styled from 'styled-components';
import { colors } from '@config/colors';
import { typography } from '@config/typography';
import { spacing } from '@config/spacing';
import { media } from '@config/breakpoints';
import {
  SlideBase,
  SlideContent,
  SlideHeader,
  SlideAccentGlow,
  SlideAccentGlowBottom,
  SegmentCard,
} from '@components/press/pitch-deck/slide-primitives';

/* -------------------------------------------------- */
/* Data                                               */
/* -------------------------------------------------- */

interface StrategyCall {
  timing: string;
  title: string;
  accent: string;
}

const CALLS: StrategyCall[] = [
  {
    timing: 'May 2026',
    title: 'AI Consumer Apps will Have a Moment in VC',
    accent: colors.accent.blue.exxtraDark,
  },
  {
    timing: 'Aug 2026',
    title: 'AI Consumer App Funding will start to Become Very Hard',
    accent: colors.accent.coral.exxtraDark,
  },
  {
    timing: 'Sept 2026',
    title: 'Hiring Early Employees will Get Much Easier',
    accent: colors.accent.green.exxtraDark,
  },
  {
    timing: 'Sept 2026',
    title: 'Imagination of What is Possible with AI Consumer Apps will Expand',
    accent: colors.accent.purple.exxtraDark,
  },
  {
    timing: '2027',
    title: 'Mega Apps Will Start to Pull Away',
    accent: colors.accent.teal.exxtraDark,
  },
  {
    timing: '2027',
    title: 'It will be a VC Funding Market, not a Founder Market',
    accent: colors.accent.orange.exxtraDark,
  },
  {
    timing: 'June 2027',
    title: 'Culling of Low Value Consumer AI & Traditional Apps by Consumers',
    accent: colors.accent.coral.exxxtraDark,
  },
  {
    timing: 'Sept 2027',
    title: 'Consumers outside of Silicon Valley will Gravitate to Mega Apps as a Budget Option',
    accent: colors.accent.blue.exxtraDark,
  },
  {
    timing: '2028',
    title: 'Culling of Low Value Consumer AI Apps by VCs',
    accent: colors.accent.coral.exxtraDark,
  },
];

/* -------------------------------------------------- */
/* Local styled components                            */
/* -------------------------------------------------- */

const CallGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${spacing.lg};
  width: 100%;

  ${media.sm} {
    grid-template-columns: repeat(2, 1fr);
  }

  ${media.md} {
    grid-template-columns: repeat(3, 1fr);
  }

  ${media.lg} {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const CallCard = styled(SegmentCard)`
  min-width: 0;
  flex: none;

  ${media.md} {
    padding: ${spacing.xl} ${spacing['2xl']};
  }
`;

const CallTiming = styled.div<{ $color: string }>`
  font-family: ${typography.fontFamily.heading};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.semibold};
  color: ${(p) => p.$color};
  text-transform: uppercase;
  letter-spacing: ${typography.letterSpacing.wide};
`;

const CallTitle = styled.h3`
  font-family: ${typography.fontFamily.heading};
  font-size: clamp(${typography.fontSize.sm}, 1.4vw, ${typography.fontSize.base});
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.highlight.dark};
  margin: 0;
  line-height: ${typography.lineHeight.tight};
`;

/* -------------------------------------------------- */
/* Component                                          */
/* -------------------------------------------------- */

export const StrategyCallsSlide: React.FC = () => (
  <SlideBase>
    <SlideAccentGlow />
    <SlideAccentGlowBottom />
    <SlideContent>
      <SlideHeader
        title="June&#x2019;s Hunches"
        subtitle="Let&#x2019;s Start Tracking Them"
      />

      <CallGrid>
        {CALLS.map((call, i) => (
          <CallCard key={i} $accent={call.accent}>
            <CallTiming $color={call.accent}>{call.timing}</CallTiming>
            <CallTitle>{call.title}</CallTitle>
          </CallCard>
        ))}
      </CallGrid>
    </SlideContent>
  </SlideBase>
);
